"use client";

import { getCurrentUser } from "@aws-amplify/auth";
import { DataLayerGA4AttrType, GA4EventAttrType } from "../types/datalayerGA";
import { usePathname } from "next/navigation";
import { getReferrer } from "./getReferrer";
import { getCookie, setDocumentCookie } from "./cookie-utils";
import { ScholarshipResultsPageParamsIF } from "@packages/lib/interfaces/scholarship";
import { getCookieValue, isWhatuni } from "@packages/lib/utlils/commonFunction";
import { GAConstants } from "@packages/shared-components/services/constants";
import { getEncryptedEmail } from "@packages/shared-components/services/utils";

function replaceSpaceWithUnderscoreAndHyphen(value: any) {
  let spaceReplacedValue =
    value != "na"
      ? value.toString().trim().toLowerCase().replaceAll(" ", "_")
      : value;
  let hyphenReplacedValue =
    spaceReplacedValue != "na"
      ? spaceReplacedValue.toString().trim().toLowerCase().replaceAll("-", "_")
      : spaceReplacedValue;
  return hyphenReplacedValue;
}

function replaceWithNA(value: any) {
  return value != null && value != undefined && value != "" ? value : GAConstants.notApplicable;
}

async function currentAuthenticatedUser() {
  try {
    const userData = await getCurrentUser();
    // Return "1" if userId exists, otherwise "0"
    return userData?.userId ? "1" : "0";
  } catch (err) {
    // Optionally return "0" if an error occurs
    return "0";
  }
}

const ucasCookieObject: any = decodeURIComponent(getCookie("UCAS") || "{}");
let ucasValue: any = {};
try {
  ucasValue = JSON.parse(ucasCookieObject);
} catch {
  ucasValue = {};
}

export function GA4DataLayerFn(dataLog: DataLayerGA4AttrType) {
  const waitForDataLayer = async () => {
    const additionalLog = dataLog?.addtionalLogs || {};
    const userIdKey =
      process.env.PROJECT == "Whatuni" ? "wu_user_id" : "pgs_user_id";
    const currentYear = new Date().getFullYear();
    const eventsObject = {
      event: replaceWithNA(dataLog.event),
      "event name": replaceWithNA(dataLog.eventName),
      data_label: processGA4Value(dataLog.data_label),
      data_label2: processGA4Value(dataLog.data_label2),
      cpe_parent_subject: processGA4Value(dataLog.cpe_parent_subject),
      cpe_child_subject: processGA4Value(dataLog.cpe_child_subject),
      page_name: replaceWithNA(
        dataLog.page_name || localStorage.getItem("gaPageName")
      ),
      college_name: processGA4Value(dataLog.college_name),
      provider_type: processGA4Value(dataLog.provider_type),
      course_name: processGA4Value(dataLog.course_name),
      sponsored_sr: processGA4Value(dataLog.sponsored_sr),
      college_id: processGA4Value(dataLog.college_id),
      ucas_points: replaceWithNA(dataLog?.ucas_points || ucasValue?.ucasPoint),
      study_mode: processGA4Value(dataLog.study_mode),
      target_year: replaceWithNA(currentYear?.toString()),
      clearing: replaceWithNA(
        dataLog.clearing || (typeof localStorage !== "undefined" && localStorage?.getItem("clearing"))
      ),
      [userIdKey]: !dataLog.user_id
        ? await currentAuthenticatedUser()
        : replaceWithNA(dataLog.user_id),
      study_level: processGA4Value(dataLog.study_level),
      article_category: processGA4Value(dataLog.article_category),
      destination_country: processGA4Value(dataLog.destination_country),
      data_label3: processGA4Value(dataLog.data_label3),
      data_label4: processGA4Value(dataLog.data_label4),
      data_label5: processGA4Value(dataLog.data_label5),
      cta_name: processGA4Value(dataLog.cta_name),
      cta_url: processGA4Value(dataLog.cta_url),
      website_name: replaceWithNA(`${process.env.PROJECT}`)?.toLowerCase(),
      contentful_1: processGA4Value(dataLog.contentful_1),
      contentful_2: processGA4Value(dataLog.contentful_2),
      page_referer: typeof document !== "undefined" && getReferrer(),
      ...(getCookie("widgetId") ? { widgetId: getCookie("widgetId") } : {}),
      ...replaceWithNA(additionalLog),
    };

    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push(eventsObject);
      return; // Stop here once found
    } else {
      setTimeout(waitForDataLayer, 200);
    }
  };

  waitForDataLayer();
}

function GADataLayerFn(
  event?: any,
  eventName?: any,
  dataLabel?: any,
  dataLabel2?: any,
  cpeParentSubject?: any,
  cpeChildSubject?: any,
  pageName?: any,
  pageNameContentful?: any,
  collegeName?: any,
  providerType?: any,
  courseName?: any,
  sponsoredSr?: any,
  collegeId?: any,
  ucasPoints?: any,
  studyMode?: any,
  targetYear?: any,
  clearing?: any,
  userId?: any,
  studyLevel?: any,
  articleCategory?: any,
  destinationCountry?: any,
  dataLabel3?: any,
  dataLabel4?: any,
  dataLabel5?: any,
  website?: any,
  ctaName?: any,
  cta_url?: any,
  contentful_1?: any,
  contentful_2?: any
) {
  const waitForDataLayer = () => {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: replaceWithNA(event),
        "event name": replaceWithNA(eventName),
        data_label: replaceWithNA(dataLabel),
        data_label2: replaceWithNA(dataLabel2),
        cpe_parent_subject: replaceWithNA(cpeParentSubject),
        cpe_child_subject: replaceWithNA(cpeChildSubject),
        page_name: replaceWithNA(pageName),
        college_name: replaceWithNA(collegeName),
        provider_type: replaceWithNA(providerType),
        course_name: replaceWithNA(courseName),
        sponsored_sr: replaceWithNA(sponsoredSr),
        college_id: replaceWithNA(collegeId),
        ucas_points: replaceWithNA(ucasValue?.ucasPoint),
        study_mode: replaceWithNA(studyMode),
        target_year: GAConstants.notApplicable,
        clearing: replaceWithNA(clearing),
        wu_user_id: userId,
        study_level: replaceWithNA(studyLevel),
        article_category: replaceWithNA(articleCategory),
        destination_country: replaceWithNA(destinationCountry),
        data_label3: replaceWithNA(dataLabel3),
        data_label4: replaceWithNA(dataLabel4),
        data_label5: replaceWithNA(dataLabel5),
        cta_name: replaceWithNA(ctaName),
        cta_url: replaceWithNA(cta_url),
        website_name: replaceWithNA(website)?.toLowerCase(),
        contentful_1: replaceWithNA(contentful_1),
        contentful_2: replaceWithNA(contentful_2),
        page_referer: getReferrer() || GAConstants.notApplicable,
      });
      return;
    } else {
      setTimeout(waitForDataLayer, 200); // Retry after 100ms
    }
  };

  waitForDataLayer();
}

function getInitialsFromJWT(token: any) {
  const email = token?.payload?.email;
  if (!email) {
    throw new Error("Email not found in token payload");
  }
  const namePart = email.split("@")[0];
  const initials = namePart
    ?.split(".")
    ?.map((part: any) => part.charAt(0).toUpperCase())
    ?.join("")
    ?.slice(0, 2);
  return initials;
}
export {
  getCookie,
  replaceSpaceWithUnderscoreAndHyphen,
  replaceWithNA,
  GADataLayerFn,
  currentAuthenticatedUser,
  getInitialsFromJWT,
};

export function formatDate(dateString: string) {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export function getArticleDetailUrlParamValues() {
  let category,
    subCategory,
    articleTitle = "";
  if (
    typeof localStorage !== "undefined" &&
    localStorage.getItem("gaPageName")?.toString() == "articleDetail"
  ) {
    let pathArr = usePathname()
      ?.split("/")
      .filter((pathvar) => pathvar != "");
    if (pathArr.length >= 3) {
      category = pathArr[0];
      subCategory = pathArr[1];
      articleTitle = pathArr[2];
    }
  }
  return { category, subCategory, articleTitle };
}

export function providerTypeWithSubOrder(suborderItemId: string) {
  return suborderItemId ? "advertiser" : "non_advertiser";
}

export function getQualTextForQualIdClient(qualId: string) {
  switch (qualId?.toUpperCase()) {
    case "M":
      return "Undergraduate";
    case "L":
      return "Postgraduate";
    case "A":
      return "Foundation degree";
    case "T":
      return "Access and foundation";
    case "N":
      return "HND/HNC";
    default:
      return "";
  }
}

export function getQualCodeClient(qualText: any) {
  switch (qualText) {
    case "degree-courses":
      return "M";
    case "postgraduate-courses":
      return "L";
    case "foundation-degree-courses":
      return "A";
    case "access-foundation-courses":
      return "T";
    case "hnd-hnc-courses":
      return "N";
    default:
      return "";
  }
}

const processGA4Value = (value: any, applyLowerCase = false) => {
  const naValue = replaceWithNA(value);
  if (naValue === GAConstants.notApplicable) return naValue;
  const processedValue = replaceSpaceWithUnderscoreAndHyphen(naValue);
  return applyLowerCase ? processedValue?.toLowerCase() : processedValue;
};


export function GA4DataLayer(dataLog: GA4EventAttrType) {
  const waitForDataLayer = async () => {
     const userID = getCookieValue("pgs_userid") || 0;
    const isAuthenticated = isWhatuni() ? await currentAuthenticatedUser() === "1" : userID !== 0;
    const pageLoadTime = typeof window !== "undefined" && window.performance?.timing?.loadEventEnd > 0
      ? Math.round((window.performance.timing.loadEventEnd - window.performance.timing.navigationStart) / 1000) 
      : Math.round(performance.now() / 1000);
    if (typeof window !== "undefined" && window.dataLayer) {
      const dataLayerObject: any = {
        event: dataLog.event || GAConstants.notApplicable,
        event_name: dataLog.event_name || GAConstants.notApplicable,
        page_name: localStorage?.getItem("gaPageName") || GAConstants.notApplicable,
        institution_name: processGA4Value(dataLog.institution_name, true),
        provider_type: processGA4Value(dataLog.provider_type, true),
        course_name: processGA4Value(dataLog.course_name, true),
        institution_id: processGA4Value(dataLog.institution_id),
        ucas_points: getCookie("ucaspoint") || "na",
        year_of_entry: isAuthenticated ? (getCookieValue(isWhatuni () ? "yearOfEntry" : "pgs_year_of_entry")) : GAConstants.notApplicable,
        cycle_phase: localStorage.getItem("clearing") || "in_year",
        study_level: processGA4Value(dataLog.study_level),
        study_mode : processGA4Value(dataLog.study_mode),
        destination_region_city: processGA4Value(dataLog.destination_region_city, true),
        website_name: processGA4Value(process.env.PROJECT, true),
        page_load_time: String(pageLoadTime),
        page_grouping:  localStorage?.getItem("pageGrouping") || GAConstants.notApplicable,
        subject_area: processGA4Value(dataLog.subject_area, true),
        subject_discipline: processGA4Value(dataLog.subject_discipline, true),
        login_status: isAuthenticated ? "logged" : "non_logged_in",
        previous_page_name: typeof window !== "undefined" && (localStorage.getItem("previousPageName") || GAConstants.notApplicable),
        click_text: processGA4Value(dataLog.click_text || GAConstants.notApplicable),
        marketing_consent: processGA4Value(dataLog.marketing_consent),
        revenue_model: processGA4Value(dataLog.revenue_model || GAConstants.notApplicable),
        revenue_value: processGA4Value(dataLog.revenue_value,true),
        page_section: processGA4Value(dataLog.page_section || GAConstants.notApplicable),
        navigation_category: processGA4Value(dataLog.navigation_category || GAConstants.notApplicable),
        navigation_subcategory: processGA4Value(dataLog.navigation_subcategory || GAConstants.notApplicable),
        field: processGA4Value(dataLog.field || GAConstants.notApplicable),
        sponsored: dataLog.sponsored || GAConstants.notApplicable,
        search_type: processGA4Value(dataLog.search_type || GAConstants.notApplicable),
        cta_url: dataLog.cta_url || GAConstants.notApplicable,
        form_type: dataLog.form_type || GAConstants.notApplicable,
        award_type: processGA4Value(dataLog.award_type,true),
        position: processGA4Value(dataLog.position,true),
        nationality: processGA4Value(dataLog.nationality,true),
        scholarship_name: processGA4Value(dataLog.scholarship_name,true),
        institution_rank: processGA4Value(dataLog.institution_rank, true)
      };
      
      if (isAuthenticated) {
          dataLayerObject.user_id = getCookieValue( isWhatuni () ? "userEmail" : "pgs_user_email");
      }
      
      window.dataLayer.push(dataLayerObject);
    } else {
      setTimeout(waitForDataLayer, 200);
    }
  };

  waitForDataLayer();
}         

export const includeTickerTape = () => {
  const pathname = usePathname();
  
  // Exclude scholarship pages
  if (pathname?.includes("/money/scholarships") || pathname?.includes("/funding/scholarships")) {
    return false;
  }
  
  if (
    pathname?.includes("/home") ||
    pathname === "/" ||
    pathname?.includes("/degrees-courses/clearing") ||
    pathname?.includes("/university-clearing/") ||
    pathname?.includes("/advice/clearing/")
  ) {
    return true;
  }
  return false;
};


export function setPreviousPageName() {
  if (typeof window !== "undefined") {
    const currentPageName = localStorage.getItem("gaPageName") || GAConstants.notApplicable;
    localStorage.setItem("previousPageName", currentPageName);
  }
}

export function getScholarshipResultsURL(
  selectedParams: ScholarshipResultsPageParamsIF
): string {
  const {
    provider,
    subject,
    studyLevel,
    award_type,
    award_coverage,
    nationality,
  } = selectedParams;
  const iswhatuni = isWhatuni();
  const hasProvider = provider && (Array.isArray(provider) ? provider.length > 0 : true);
  const hasSubject = subject && (Array.isArray(subject) ? subject.length > 0 : true);
  const hasStudyLevel = studyLevel && iswhatuni;
  
  // Check if we have any path-based filters
  const hasPathFilters = hasProvider || hasSubject || hasStudyLevel;
  
  // For PGS, if no path filters, return base URL
  if (!iswhatuni && !hasPathFilters) {
    const params = new URLSearchParams();
    if (award_type) params.set("award-type", award_type);
    if (award_coverage) params.set("award-coverage", award_coverage);
    if (nationality) params.set("nationality", nationality);
    const queryString = params.toString();
    return queryString ? `/funding/scholarships?${queryString}` : '/funding/scholarships';
  }
  
  let finalURL = `/${iswhatuni ? "money" : "funding"}/scholarships/${hasProvider ? "csearch" : "search"}`;

  if (studyLevel && iswhatuni) finalURL += `/${studyLevel}`;
  if (hasSubject) finalURL += `/${subject}`;
  if (hasProvider) finalURL += `/${provider}`;

  const params = new URLSearchParams();
  if (award_type) params.set("award-type", award_type);
  if (award_coverage) params.set("award-coverage", award_coverage);
  if (nationality) params.set("nationality", nationality);

  const queryString = params.toString();
  return queryString ? `${finalURL}?${queryString}` : finalURL;
}

